import PageHero from "../components/PageHero";
import PricingFaq from "./faq";

const MODELS = [
  {
    slug: "nomad-one",
    name: "Nomad One",
    type: "Compact Cabin",
    area: "360 sq ft",
    bedrooms: "1 Bed",
    price: "₹52 L",
    roi: "26%",
    style: "Airbnb",
  },
  {
    slug: "aurora",
    name: "Aurora",
    type: "Studio A-Frame",
    area: "420 sq ft",
    bedrooms: "1 Bed",
    price: "₹66 L",
    roi: "28%",
    style: "Staycation",
  },
  {
    slug: "havenside",
    name: "Havenside",
    type: "2 BHK Family",
    area: "720 sq ft",
    bedrooms: "2 Beds",
    price: "₹98 L",
    roi: "32%",
    style: "Family",
  },
  {
    slug: "slopeview",
    name: "Slopeview",
    type: "Premium Glass",
    area: "860 sq ft",
    bedrooms: "2 Beds",
    price: "₹1.2 Cr",
    roi: "34%",
    style: "Airbnb",
  },
  {
    slug: "crestline",
    name: "Crestline",
    type: "3 BHK Resort",
    area: "980 sq ft",
    bedrooms: "3 Beds",
    price: "₹1.45 Cr",
    roi: "38%",
    style: "Resort",
  },
  {
    slug: "ridgetop",
    name: "Ridgetop",
    type: "Dual Storey",
    area: "1240 sq ft",
    bedrooms: "3 Beds",
    price: "₹1.8 Cr",
    roi: "40%",
    style: "Resort",
  },
];

const OWNERSHIP = [
  {
    icon: "ri-vip-crown-line",
    title: "Direct Ownership",
    desc: "Buy a villa outright in your name, manage it yourself or let us run it on the hospitality network.",
  },
  {
    icon: "ri-team-line",
    title: "Co-Ownership",
    desc: "Split cost and usage with partners or family — ideal for group holiday homes with shared income.",
  },
  {
    icon: "ri-funds-line",
    title: "Resort Cluster",
    desc: "Invest across a cluster of villas for higher scale, shared operations and diversified occupancy.",
  },
  {
    icon: "ri-hand-coin-line",
    title: "Staged Payments",
    desc: "Pay in construction-linked instalments so capital is deployed gradually through the build.",
  },
];

export default function Pricing() {
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Pricing & Returns"
        title={
          <>
            Villa Models, <span className="italic text-accent-300">Priced & Compared</span>
          </>
        }
        subtitle="A transparent look at every villa model — from entry price to estimated annual returns — so you can pick what fits your budget and goals."
      />

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
          <div className="overflow-x-auto rounded-2xl bg-background-100 border border-background-200">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  {["Model", "Type", "Area", "Starting Price", "Est. ROI", "Use Style"].map((h) => (
                    <th
                      key={h}
                      className="px-5 py-4 text-xs font-semibold uppercase tracking-wide text-foreground-600 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody>
                {MODELS.map((m, i) => (
                  <tr
                    key={m.name}
                    className={`border-b border-background-200 last:border-0 ${
                      i % 2 === 0 ? "bg-background-50" : "bg-background-100"
                    }`}
                  >
                    <td className="px-5 py-4">
                      <p className="font-heading font-semibold text-foreground-950">{m.name}</p>
                      <p className="text-xs text-foreground-600">{m.type}</p>
                    </td>
                    <td className="px-5 py-4 text-sm text-foreground-700 whitespace-nowrap">{m.bedrooms}</td>
                    <td className="px-5 py-4 text-sm text-foreground-700 whitespace-nowrap">{m.area}</td>
                    <td className="px-5 py-4 text-sm font-semibold text-foreground-950 whitespace-nowrap">{m.price}</td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 text-primary-800 text-xs font-bold px-3 py-1">
                        {m.roi} / yr
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-foreground-700 whitespace-nowrap">{m.style}</td>
                    <td className="px-5 py-4">
                      <a
                        href={m.slug ? `/villa/${m.slug}` : `/contact?villa=${encodeURIComponent(m.name)}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap text-primary-700 hover:text-primary-800"
                      >
                        {m.slug ? "View Details" : "Enquire"} <i className="ri-arrow-right-line"></i>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-xs text-foreground-600 mt-5">
            Estimated ROI is indicative and based on modelled occupancy across our managed hospitality network.
            Actual returns vary by location, seasonality and operating costs.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {OWNERSHIP.map((o) => (
              <div
                key={o.title}
                className="rounded-2xl bg-background-100 border border-background-200 p-6 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-100 text-primary-700">
                  <i className={`${o.icon} text-2xl`}></i>
                </span>
                <h4 className="font-heading text-lg text-foreground-950 mt-4">{o.title}</h4>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-primary-100 border border-primary-200 p-6 md:p-8 text-center">
            <h4 className="font-heading text-xl text-foreground-950">Not sure which model fits?</h4>
            <p className="text-sm text-foreground-700 mt-2">
              Download the 2026 investment guide for full specs, returns and the complete purchase journey.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/investment-guide"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700 transition-colors"
              >
                Download Guide <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-primary-600 text-primary-700 px-6 py-3 hover:bg-primary-600 hover:text-background-50 transition-colors"
              >
                Talk to an Advisor <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>
      <PricingFaq />
    </div>
  );
}